import React from 'react';
import type { StoryPhoto } from './StoryGallery';
import type { LegacyMilestone } from '../data/legacyMilestones';

type LegacyTimelineSidebarProps = {
  milestones: LegacyMilestone[];
  activeIndex: number;
  activePhotoIndex: number;
  onSelectYear: (index: number) => void;
  onSelectPhoto: (yearIndex: number, photoIndex: number) => void;
};

const LegacyTimelineSidebar: React.FC<LegacyTimelineSidebarProps> = ({
  milestones,
  activeIndex,
  activePhotoIndex,
  onSelectYear,
  onSelectPhoto,
}) => {
  return (
    <div className="flex flex-col gap-5 text-white/75">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] uppercase tracking-[0.5em] text-emerald-200 drop-shadow timeline-heading">
          Program Timeline
        </p>
      </div>
      <div className="flex flex-row md:flex-col gap-4 md:gap-3 text-xs uppercase tracking-[0.3em] overflow-x-auto md:overflow-visible pb-2 md:pb-0">
        {milestones.map((milestone, index) => {
          const isActive = index === activeIndex;
          const photoCount = ((milestone.photos as StoryPhoto[]) ?? []).length;
          return (
            <button
              type="button"
              key={`${milestone.year}-sidebar`}
              onClick={(e) => {
                e.preventDefault();
                onSelectYear(index);
              }}
              className={`text-left min-w-[120px] md:min-w-0 transition-all ${
                isActive ? 'text-emerald-200 drop-shadow-lg font-semibold' : 'text-white/45'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="px-1 timeline-year">{milestone.year}</span>
                {photoCount > 0 && (
                  <span className="flex items-center gap-1 opacity-70 pointer-events-none timeline-dots">
                    {Array.from({ length: photoCount }).map((_, dotIdx) => {
                      const isDotActive = isActive && dotIdx === activePhotoIndex;
                      return (
                        <span
                          key={`${milestone.year}-dot-${dotIdx}`}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelectPhoto(index, dotIdx);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;
                            event.preventDefault();
                            event.stopPropagation();
                            onSelectPhoto(index, dotIdx);
                          }}
                          className={`w-1.5 h-1.5 rounded-full ${
                            isDotActive ? 'bg-emerald-300 shadow-[0_0_6px_rgba(16,185,129,0.8)]' : 'bg-white/30'
                          }`}
                          aria-pressed={isDotActive}
                          aria-label={`Go to photo ${dotIdx + 1} of ${milestone.year}`}
                        />
                      );
                    })}
                  </span>
                )}
              </span>
              {isActive && (
                <span className="block text-[10px] uppercase tracking-[0.35em] mt-2 timeline-title">
                  {milestone.title}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LegacyTimelineSidebar;
